// Utilities over the server's classifier_notes string:
//  - parseClassifierNotes(): extract the human "base" rationale, dropping the
//    "Check first:" hint list and the " | sub-cause:" tail.
//  - deriveEvidence(): turn the decisive signal fragments into evidence chips.
// The evidence chips (deriveEvidence) are the single source of the structured
// "why this class" signals shown in the UI; the notes string's own "Check
// first" hint list is not separately parsed anymore.

import { triageColors } from '../theme/antd-theme';

export type ParsedClassifierNotes = {
  /** Base rationale, minus the "Check first:" hint list and sub-cause tail. */
  base: string;
};

// --- Evidence strip -------------------------------------------------------
// The classifier bakes its decisive MEASUREMENT into every rule's note
// (offline days, battery mAh/%/overdue, dBm, OTA fails, cohort sizes, prior
// events, test-jig, recovery, node age). deriveEvidence() surfaces those as
// value-carrying chips for EVERY class — not just the class-10 "Check first"
// hints parseClassifierNotes() handles.
//
// Robust-by-SIGNAL, not by-rule: each scanner targets one stable measurement
// fragment (a number + unit), of which there are ~12 — far fewer and far more
// stable than the ~40 rule note templates. A rule reword at worst drops a chip
// (the prose note still renders below) instead of ever showing wrong data.
export type Evidence = {
  kind: string;
  /** Short chip label, e.g. "battery +14d overdue". */
  label: string;
  /** Full context for the chip tooltip. */
  tooltip: string;
  /** antd Tag color, themed to the related class. */
  color: string;
};

type EvidenceScanner = {
  kind: string;
  color: string;
  // When `skip` matches the note, the whole scanner is suppressed — used to
  // honor explicit negations ("no drain signature", "no EOL … signature") so a
  // chip never asserts a cause the classifier deliberately ruled out.
  skip?: RegExp;
  // First pattern that matches wins; `label` builds the chip text from the
  // match, `tooltip` defaults to the whole matched fragment.
  patterns: Array<{ re: RegExp; label: (m: RegExpMatchArray) => string; tip?: (m: RegExpMatchArray) => string }>;
};

// Neutral tag (antd 'default' preset — light outline) for signals that don't map
// to a single class (dropout pattern, offline duration). Deliberately NOT a
// filled gray: triageColors.actionOnSite is #8c8c8c, so a filled gray here would
// be indistinguishable from a real on-site (cohort/gateway) chip.
const EVIDENCE_NEUTRAL = 'default';

// Ordered by "why" relevance: cause signals first, offline duration last so it
// fills a remaining slot rather than crowding out the decisive measurement.
// Chip color follows the SIGNAL's typical class; genuinely class-ambiguous
// signals (dropout, offline) stay neutral so they never imply a cause.
const EVIDENCE_SCANNERS: EvidenceScanner[] = [
  {
    kind: 'battery', color: triageColors.batteryDrained,
    patterns: [
      { re: /past (?:predicted )?life by (\d+)\s*d/i, label: (m) => `battery +${m[1]}d overdue` },
      // Server "Check first" hint: "battery near/past end (5d predicted)".
      { re: /near\/past end \((-?\d+)\s*d/i, label: (m) => `battery ${m[1]}d to end` },
      { re: /low battery \((\d+)\s*%/i, label: (m) => `battery ${m[1]}%` },
      { re: /change batteries/i, label: () => 'change batteries' },
      { re: /forecast confirms low battery|battery (?:long )?dead|battery deplet(?:ed|ion)/i, label: () => 'battery low' },
    ],
  },
  {
    // Excess current draw is a HARDWARE signal (matches subclassTagMap.hw_drain),
    // not battery depletion — kept a distinct kind so its chip is HW-colored.
    // Suppressed when the note explicitly says there's NO drain signature
    // (R-ENRICHMENT-HW demote-to-on-site: "only N mAh excess … (no drain signature)").
    kind: 'drain', color: triageColors.hardwareFault, skip: /no drain signature/i,
    patterns: [{ re: /(\d+)\s*mAh\s+excess/i, label: (m) => `${m[1]}mAh excess draw` }],
  },
  {
    // High rejoin rate draining the battery (R-JOIN-DRAIN / the join hint).
    // Battery-colored — it's a battery-depletion driver, not a hardware fault.
    kind: 'rejoin', color: triageColors.batteryDrained,
    patterns: [
      { re: /(\d+)\s*joins?\/day/i, label: (m) => `${m[1]}/day rejoins` },
      { re: /rejoin rate \((\d+)\/day/i, label: (m) => `${m[1]}/day rejoins` },
    ],
  },
  {
    // Node age is written by the classifier with one decimal (e.g. "3.2y
    // deployed", "4.7y + …") — capture the decimal, and both "…y deployed" and
    // the "…y +" aged-cohort form. Scoped to those two shapes (not a bare "Ny")
    // so an incidental digit-then-y token can't be misread as node age.
    // Suppressed when the note explicitly rules out EOL ("no EOL … signature") —
    // the properties count there is cohort breadth, not an end-of-life signal.
    // Ranked high (a decisive EOL signal) so it leads over the on-site gateway
    // chip on aged-EOL escalations of a gateway-offline note.
    kind: 'age', color: triageColors.hardwareFaultEol, skip: /no EOL/i,
    patterns: [
      { re: /(\d+(?:\.\d+)?)y\b[^;|]*?deployed/i, label: (m) => `${m[1]}y deployed` },
      { re: /(\d+(?:\.\d+)?)y\s*\+/i, label: (m) => `${m[1]}y deployed` },
      { re: /(\d+)\s+properties/i, label: (m) => `${m[1]} properties` },
    ],
  },
  {
    kind: 'firmware', color: triageColors.actionFirmware,
    patterns: [
      { re: /(\d+)\s+failed\s+OTA/i, label: (m) => `${m[1]}× OTA fail` },
      { re: /firmware regression/i, label: () => 'fw regression' },
      // Word-bounded so "no failures" / "failures" can't false-trigger a fail chip.
      { re: /\bOTA\b[^;|]*?\b(?:failed|fail|error|timeout)\b/i, label: () => 'OTA fail' },
      { re: /\bOTA\b[^;|]*?\bcompleted\b/i, label: () => 'recent OTA' },
    ],
  },
  {
    kind: 'signal', color: triageColors.actionOutOfRange,
    patterns: [
      // Only emit a weak-signal chip when the note actually says the signal was
      // WEAK — a bare dBm regex also matched notes calling the signal "healthy".
      { re: /weak signal[^;|]*?(-?\d{2,3})\s*dBm/i, label: (m) => `${m[1]} dBm weak` },
      { re: /degrading[^;|]*?\(([-\d.]+)\/day\)/i, label: (m) => `drift ${m[1]}/d` },
      // signal_drift sub-cause tail: "(slope -0.60/day, degrading before offline)"
      // — the slope precedes "degrading", so the pattern above can't reach it.
      { re: /slope\s+([-\d.]+)\s*\/\s*day/i, label: (m) => `drift ${m[1]}/d` },
      { re: /signal degrading/i, label: () => 'signal drift' },
    ],
  },
  {
    kind: 'testjig', color: triageColors.hardwareFault,
    patterns: [{ re: /test[- ]?jig failure(?:\s+on\s+(\S+))?/i, label: () => 'test-jig fail' }],
  },
  {
    kind: 'cohort', color: triageColors.actionOnSite,
    patterns: [
      { re: /gateway cohort:\s*(\d+)/i, label: (m) => `${m[1]} on gateway` },
      { re: /(\d+)\s+(?:other\s+)?(?:peer\s+)?nodes?\b[^;|]*?same gateway/i, label: (m) => `${m[1]} on gateway` },
      { re: /location-wide event:\s*(\d+)/i, label: (m) => `${m[1]} co-offline` },
      { re: /batch dropout \((\d+)/i, label: (m) => `${m[1]} co-offline` },
      // "same window" and the tamper-suspect "same ±15min window" variant.
      { re: /(\d+)\s+nodes?\b[^;|]*?(?:dropped together|same[^;|]*?window)/i, label: (m) => `${m[1]} co-offline` },
    ],
  },
  {
    kind: 'gateway', color: triageColors.actionOnSite,
    patterns: [{ re: /gateway (?:at this location )?(?:is )?offline/i, label: () => 'gateway offline' }],
  },
  {
    kind: 'repeat', color: triageColors.hardwareFault,
    patterns: [
      { re: /repeat offender \((\d+\+?)\s+events?\)/i, label: (m) => `${m[1]} prior events` },
      { re: /(\d+)\s+(?:prior\s+)?offline events/i, label: (m) => `${m[1]} prior events` },
      // R-ENRICHMENT-CHRONIC writes "(N prior events…)" (no "offline"/"repeat offender").
      { re: /(\d+)\s+prior events/i, label: (m) => `${m[1]} prior events` },
      { re: /(\d+)\s+closed tickets/i, label: (m) => `${m[1]} prior tickets` },
    ],
  },
  {
    // Dropout pattern spans OUT_OF_RANGE (R-SUDDEN-WEAK) and TAMPERING
    // (R-TAMPER-SUDDEN) — neutral color + faithful label (don't hardcode "cutoff").
    kind: 'dropout', color: EVIDENCE_NEUTRAL,
    patterns: [{ re: /(sudden|gradual)\s+(cutoff|dropout)/i, label: (m) => `${m[1].toLowerCase()} ${m[2].toLowerCase()}` }],
  },
  {
    kind: 'remap', color: triageColors.actionOutOfRange,
    patterns: [{ re: /re-?mapped (\d+)\s*d/i, label: (m) => `remapped ${m[1]}d` }],
  },
  {
    kind: 'recovery', color: triageColors.offlineUnknown,
    patterns: [{ re: /recovered in (\d+)\s*d/i, label: (m) => `recovered ${m[1]}d` }],
  },
  {
    // Class-10 "Check first" enrichment cue — parity with the old parsed.hints
    // chip that the switch to deriveEvidence would otherwise have dropped.
    // Stop the capture at the "; " hint delimiter (and "|" sub-cause tail) — a
    // bare \S+ would swallow the trailing ";" when another hint follows.
    kind: 'enrichment', color: triageColors.actionOnSite,
    patterns: [{ re: /enrichment=([^\s;|]+)/i, label: (m) => `enrich: ${m[1].toLowerCase()}` }],
  },
  {
    // Offline duration echoes the classifier's OWN stated silence ("silent Nd" /
    // "Stale Nd") rather than prod nodes.last_seen — which is join-polluted and
    // only ~2mo-retained, so it must not be the source of an authoritative chip.
    // Last in the list so a concrete cause signal always ranks ahead of it.
    kind: 'offline', color: EVIDENCE_NEUTRAL,
    patterns: [
      { re: /silent (\d+)\s*d\b/i, label: (m) => `offline ${m[1]}d` },
      { re: /stale (\d+)\s*d\b/i, label: (m) => `offline ${m[1]}d` },
    ],
  },
];

export function deriveEvidence(opts: {
  notes: string | null;
  max?: number;
}): Evidence[] {
  const notes = opts.notes ?? '';
  const max = opts.max ?? 4;
  const out: Evidence[] = [];

  for (const scanner of EVIDENCE_SCANNERS) {
    if (scanner.skip && scanner.skip.test(notes)) continue;
    for (const p of scanner.patterns) {
      const m = notes.match(p.re);
      if (m) {
        out.push({
          kind: scanner.kind,
          label: p.label(m),
          tooltip: (p.tip ? p.tip(m) : m[0]).trim(),
          color: scanner.color,
        });
        break; // one chip per kind
      }
    }
  }

  return out.slice(0, max);
}

// Extract the human "base" rationale: drop the " | sub-cause:" tail and the
// " Check first:" hint list (the structured signals now come from
// deriveEvidence, not from re-parsing the hint list here).
export function parseClassifierNotes(notes: string | null): ParsedClassifierNotes {
  if (!notes) return { base: '' };

  let working = notes;
  const subIdx = working.lastIndexOf(' | sub-cause:');
  if (subIdx !== -1) working = working.slice(0, subIdx).trim();

  const checkIdx = working.indexOf(' Check first:');
  if (checkIdx === -1) return { base: working };

  return { base: working.slice(0, checkIdx).replace(/[.;]\s*$/, '').trim() };
}
